import { type FC, useEffect } from 'react'
import { useFormContext } from 'react-hook-form'
import { FormattedMessage, useIntl } from 'react-intl'

import { type UserSubOrderV2 } from '@redteclab/api/clients/bully'

import { CONTACT_FORM_SUBJECT_KEY } from '../../model/directContactFormSubjectCatalog'
import {
  directContactFormSubjectPolicyShouldShowMessage,
  type ResolvedContactFormSubject,
} from '../../model/directContactFormSubjectPolicySelectors'
import { DirectContactFormCustomerFields } from '../DirectContactFormFields/DirectContactFormCustomerFields'
import { DirectContactFormMessageField } from '../DirectContactFormFields/DirectContactFormMessageField'
import { DirectContactFormPhoneNumberField } from '../DirectContactFormFields/DirectContactFormPhoneNumberField'
import { DirectContactFormProductCodeField } from '../DirectContactFormFields/DirectContactFormProductCodeField'
import { DirectContactFormSubjectField } from '../DirectContactFormFields/DirectContactFormSubjectField'
import { DirectContactFormTextField } from '../DirectContactFormFields/DirectContactFormTextField'

import { DirectContactFormCopaymentExemption } from './DirectContactFormCopaymentExemption'
import { DirectContactFormHookFormDialogDoctorOrderFields } from './DirectContactFormHookFormDialogDoctorOrderFields'
import {
  type DirectContactFormHookFormDialogProduct,
  type DirectContactFormHookFormDialogValues,
} from './DirectContactFormHookFormDialogModel'

export type DirectContactFormHookFormFieldsProps = {
  authentication: {
    isCustomerDataComplete: boolean
    isLoggedIn: boolean
  }
  configuration: {
    disableSubject?: boolean
    isSubjectPharmaRelated?: boolean
    prefilledSubOrder?: UserSubOrderV2
    product?: DirectContactFormHookFormDialogProduct
    resolvedSubject: ResolvedContactFormSubject
  }
}

export const DirectContactFormHookFormFields: FC<
  DirectContactFormHookFormFieldsProps
> = ({
  authentication: { isCustomerDataComplete, isLoggedIn },
  configuration: {
    disableSubject,
    isSubjectPharmaRelated,
    prefilledSubOrder,
    product,
    resolvedSubject,
  },
}) => {
  const intl = useIntl()
  const { clearErrors, resetField } =
    useFormContext<DirectContactFormHookFormDialogValues>()
  const requiredMessage = intl.formatMessage({ id: 'form.error.input.missing' })
  const isCancelContract =
    resolvedSubject.key === CONTACT_FORM_SUBJECT_KEY.CANCEL_CONTRACT
  const isCopaymentExemption =
    resolvedSubject.key === CONTACT_FORM_SUBJECT_KEY.COPAYMENT_EXEMPTION
  const isDoctorOrder =
    resolvedSubject.key === CONTACT_FORM_SUBJECT_KEY.DOCTOR_ORDER
  const shouldShowOptionalOrderNumber =
    resolvedSubject.policy.orderNumberRequirement === 'optional'
  const shouldShowOrderNumber =
    resolvedSubject.policy.orderNumberRequirement === 'required'
  const shouldRenderOrderNumber =
    shouldShowOrderNumber || (!isLoggedIn && shouldShowOptionalOrderNumber)
  const shouldShowMessage = directContactFormSubjectPolicyShouldShowMessage({
    isLoggedIn,
    visibility: resolvedSubject.policy.sharedFormMessageVisibility,
  })
  const handleSubjectChange = (): void => {
    clearErrors(['subject', 'message'])
    resetField('images')
    resetField('orderId')
    resetField('productCode')
  }

  useEffect(() => {
    if (!isLoggedIn && shouldShowOptionalOrderNumber) {
      clearErrors('orderId')
    }
  }, [clearErrors, isLoggedIn, shouldShowOptionalOrderNumber])

  return (
    <>
      <DirectContactFormCustomerFields
        isCustomerDataComplete={isCustomerDataComplete}
      />
      <DirectContactFormPhoneNumberField />
      <DirectContactFormSubjectField
        disabled={disableSubject}
        includePrefilledOption
        isSubjectPharmaRelated={isSubjectPharmaRelated}
        onSubjectChange={handleSubjectChange}
      />
      <DirectContactFormProductCodeField
        isPrefilled={Boolean(product)}
        resolvedSubject={resolvedSubject}
      />
      {shouldRenderOrderNumber ? (
        <DirectContactFormTextField
          data-qa-id="contact-form-orderId"
          disabled={Boolean(prefilledSubOrder)}
          label={intl.formatMessage({ id: 'general.label.ordernumber' })}
          name="orderId"
          required={shouldShowOrderNumber}
          rules={{
            required: shouldShowOrderNumber && requiredMessage,
          }}
        />
      ) : null}
      {isCancelContract ? (
        <p>
          <FormattedMessage id="general.label.cancellationConfirmationEmail" />
        </p>
      ) : null}
      <DirectContactFormMessageField isVisible={shouldShowMessage} />
      <DirectContactFormHookFormDialogDoctorOrderFields
        isDoctorOrder={isDoctorOrder}
        isLoggedIn={isLoggedIn}
      />
      <DirectContactFormCopaymentExemption
        isCopaymentExemption={isCopaymentExemption}
        isLoggedIn={isLoggedIn}
      />
    </>
  )
}
